import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  Trophy, 
  Download, 
  Share2, 
  ExternalLink,
  CheckCircle,
  Award,
  Medal
} from "lucide-react";

function GameCertificate({ certificate, onClose }) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);

  if (!certificate) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Use html2canvas to capture the certificate
      const html2canvas = (await import('html2canvas')).default;
      const element = document.getElementById('game-certificate');
      if (!element) return;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff'
      });
      
      const link = document.createElement('a');
      link.download = `${certificate.business_name || 'certificate'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${certificate.participant_name}'s Business Certificate`,
          text: `Check out my Lemonade Stand Business Plan certificate! Completed ${certificate.module_completed} with ${certificate.total_points} points.`,
          url: certificate.verification_url
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(certificate.verification_url);
      alert('Verification link copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Certificate */}
        <div 
          id="game-certificate" 
          className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-block bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
              www.lemonadestandbusinessplan.com
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Certificate of Completion
            </h1>
            <p className="text-gray-600">This certifies that</p>
          </div>

          {/* Participant Name */}
          <div className="text-center mb-6">
            <h2 className="text-4xl font-bold text-yellow-600 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              {certificate.participant_name}
            </h2>
            <p className="text-gray-600">
              has successfully completed the
            </p>
          </div>

          {/* Module */}
          <div className="bg-white rounded-xl p-6 mb-6 shadow-md">
            <div className="text-center">
              <div className="text-5xl mb-3">🍋</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {certificate.module_completed}
              </h3>
              <p className="text-gray-500">
                Lemonade Stand Business Plan Program
              </p>
            </div>
          </div>

          {/* Business Name */}
          {certificate.business_name && (
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 mb-1">Business:</p>
              <p className="text-xl font-bold text-gray-800">
                {certificate.business_name}
              </p>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-900">
                {certificate.total_points}
              </div>
              <div className="text-xs text-gray-500">Points Earned</div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <Medal className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-900">
                #{certificate.rank}
              </div>
              <div className="text-xs text-gray-500">
                of {certificate.total_participants}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <Award className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <div className="text-lg font-bold text-gray-900">
                {certificate.disc_type ? (
                  <>
                    {certificate.disc_type === 'eagle' && '🦅'}
                    {certificate.disc_type === 'parrot' && '🦜'}
                    {certificate.disc_type === 'dove' && '🕊️'}
                    {certificate.disc_type === 'owl' && '🦉'}
                  </>
                ) : (
                  '✓'
                )}
              </div>
              <div className="text-xs text-gray-500">
                {certificate.disc_type ? 'DISC Type' : 'Verified'}
              </div>
            </div>
          </div>

          {/* QR Code & Verification */}
          <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm">
            <div className="flex-1">
              <p className="text-xs text-gray-500 mb-1">Certificate ID</p>
              <p className="font-mono text-sm text-gray-700">
                {certificate.certificate_id}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {new Date(certificate.completion_date).toLocaleDateString()}
              </p>
            </div>
            <div className="w-24 h-24">
              <img 
                src={certificate.qr_code_base64} 
                alt="Verification QR Code"
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6 pt-4 border-t border-yellow-200">
            <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
              <span>Brandon Ward – Founder</span>
              <span>|</span>
              <span>🍋 LemonBot – AI Coach</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              A The WE Mentality Product • www.thewementality.com
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-gray-50 flex gap-3 justify-center">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-full font-semibold flex items-center gap-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Download'}
          </button>
          <button
            onClick={handleShare}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-full font-semibold flex items-center gap-2 transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <a
            href={certificate.verification_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-full font-semibold flex items-center gap-2 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Verify
          </a>
        </div>

        {/* Close Button */}
        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default GameCertificate;
